/**
 * Buscar un cliente en la copia que guarda el dispositivo.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 *  POR QUÉ ESTA BÚSQUEDA ES LOCAL SIEMPRE, Y NO SOLO SIN INTERNET
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Es la misma decisión que en el catálogo del punto de venta, y por los mismos
 * dos motivos:
 *
 *   · **Velocidad.** Escribir una cédula y esperar 300 ms al servidor se siente
 *     lento con alguien delante. En memoria es instantáneo.
 *   · **Un solo comportamiento.** Si con internet buscara en el servidor y sin
 *     internet en el dispositivo, el cajero vería resultados distintos según la
 *     señal. Y eso, en la pantalla de «¿a quién le fío?», acaba en dos fichas
 *     del mismo señor.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 *  ⚠️ TIENE QUE ENCONTRAR LO MISMO QUE EL SERVIDOR
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * El servidor busca por nombre sin tildes, por teléfono tal cual, y por cédula
 * NORMALIZADA —sin puntos, guiones ni espacios— (ver `consulta_de_clientes` en
 * `modules/pos/service.py`). Aquí se repite esa regla.
 *
 * La de la cédula es la que importa: la misma persona escribe su documento de
 * cinco formas —«12345678», «12.345.678», «V-12345678»— y si la búsqueda local
 * fuera literal, el cajero no encontraría al cliente que sí está guardado, lo
 * registraría otra vez, y su deuda quedaría partida en dos fichas.
 */

import { contieneSinTildes, sinTildes } from "@/lib/sinTildes";
import type { Customer } from "@/lib/types";

/**
 * La cédula lista para buscar: **solo los números**.
 *
 *     "V-12.345.678"  ->  "12345678"
 *     "12345678"      ->  "12345678"
 *     "v12345678"     ->  "12345678"
 *     "J-40123456-7"  ->  "401234567"
 *
 * ⚠️ TIENE QUE APLANAR EXACTAMENTE IGUAL QUE `normalizar_documento` EN EL
 * SERVIDOR (`modules/pos/service.py`), incluida la parte que sorprende: **la
 * letra se tira**.
 *
 * La razón es de allá y vale igual aquí: en Venezuela media gente dice «doce
 * millones trescientos» y la otra media «V doce millones trescientos».
 * Guardando la letra, «V-12.345.678» y «12345678» serían dos personas
 * distintas y el cajero acabaría creando la ficha duplicada que todo esto viene
 * a evitar.
 *
 * Y si aquí se conservara la letra y allá no, pasaría lo mismo pero peor:
 * buscar «V12345678» encontraría al cliente con internet y no lo encontraría
 * sin él. La letra sigue guardada en `tax_id` tal como se escribió, que es lo
 * que va impreso en la factura.
 */
export function normalizarDocumento(valor: string | null | undefined): string {
  if (!valor) return "";
  return valor.replace(/\D/g, "");
}

const COMPARAR = new Intl.Collator("es").compare;

/**
 * Los clientes por nombre, sin que las tildes cambien el orden.
 *
 * ⚠️ Cada nombre se aplana UNA vez. Antes se aplanaban los dos en cada
 * comparación: con cinco mil clientes no se notaba, pero sin tope pueden ser
 * cincuenta mil, y eran más de un millón de aplanados cada vez que se abría el
 * buscador.
 */
export function ordenarClientes(todos: Customer[]): Customer[] {
  return todos
    .map((c) => ({ c, clave: sinTildes(c.name) }))
    .sort((a, b) => COMPARAR(a.clave, b.clave))
    .map((x) => x.c);
}

/**
 * Los clientes que coinciden con lo escrito, ordenados por nombre.
 *
 * Sin texto devuelve la lista entera, que es lo que hace falta al abrir el
 * selector: enseñar a quién se le puede vender, no una pantalla en blanco
 * esperando a que alguien teclee.
 */
export function buscarClientes(
  todos: Customer[],
  texto: string,
  tope = 30,
  { yaOrdenados = false }: { yaOrdenados?: boolean } = {},
): Customer[] {
  const buscado = texto.trim();
  const ordenados = yaOrdenados ? todos : ordenarClientes(todos);

  if (!buscado) return ordenados.slice(0, tope);

  const documento = normalizarDocumento(buscado);

  return ordenados
    .filter(
      (c) =>
        contieneSinTildes(c.name, buscado) ||
        (c.phone ? c.phone.includes(buscado) : false) ||
        /*
          ⚠️ El documento, comparado normalizado por los dos lados.

          Y solo si lo escrito tiene algo después de quitarle los adornos: sin
          esta guarda, buscar «-» dejaría el documento en vacío y `includes("")`
          es cierto para todo el mundo — el buscador devolvería el negocio
          entero y el cajero elegiría a quien no era.
        */
        (documento.length > 0 && normalizarDocumento(c.tax_id).includes(documento)),
    )
    .slice(0, tope);
}

/**
 * ¿Ya hay alguien registrado con esa cédula?
 *
 * ⚠️ ESTA COMPROBACIÓN ES LA QUE FALTABA SIN INTERNET.
 *
 * Con señal la hace el servidor: registrar una cédula que ya existe devuelve al
 * cliente de siempre en vez de crear un duplicado. Sin señal no hay quién la
 * haga, y sin esto el cajero crearía la segunda ficha de doña Carmen — con su
 * deuda repartida entre las dos, que es exactamente lo que la cédula
 * normalizada vino a evitar.
 */
export function clienteConEseDocumento(
  todos: Customer[],
  documento: string | null | undefined,
): Customer | null {
  const buscado = normalizarDocumento(documento);
  if (!buscado) return null;
  return todos.find((c) => normalizarDocumento(c.tax_id) === buscado) ?? null;
}
